/**
 * Claiming a scanned onboarding code out of the URL (DECISIONS D-013).
 *
 * The surveyor points their camera at the QR on the office screen and taps the
 * notification. The browser opens `https://…/#o=<secret>`. This module is what
 * happens in the first few milliseconds after that.
 *
 * ── Three properties, each of them load-bearing ─────────────────────────────
 *
 * 1. **The secret leaves the URL before anything renders.** `replaceState`,
 *    never a push, so Back cannot walk to an address that still holds it, and a
 *    screenshot of the address bar is not a credential. Same treatment
 *    `claimTokenFromLocation()` gives an assignment token, for the same reason.
 *
 * 2. **It is never written to storage.** A token is kept because the device
 *    needs it forever; a setup code is spent within a second of arriving and
 *    can never be used again. Persisting it would leave a dead secret on the
 *    phone and add nothing — so it lives in a module variable that dies with
 *    the page.
 *
 * 3. **It is redeemed exactly once per page load.** This is the trap, and it is
 *    not hypothetical: React StrictMode runs effects twice in development, and
 *    a screen that redeemed in an effect would fire two requests, the second of
 *    which is correctly refused as `onboarding_link_used`. A working code would
 *    report itself as already spent. The redemption is therefore memoised HERE,
 *    outside React, as one promise per secret — every caller awaits the same
 *    request, and a re-render cannot start a second one. A FAILED attempt is
 *    forgotten, so Try Again is a button that can actually work; see `once`.
 *
 * ── And since D-019, the app can be handed a code without being opened by one ─
 * iOS gives a Home Screen web app a storage container separate from Safari's,
 * so a link tapped in Messages links Safari and leaves the installed app — the
 * one used in the field — unlinked with no way to fix itself. The second half
 * of this file is that way: a code scanned by the app's own camera, or nine
 * letters typed into it, redeemed through the same memo and the same client.
 */

import { ONBOARDING_PARAM, looksLikeOnboardingSecret } from '../../core/onboarding';
import { looksLikePairCode, normalizePairCode } from '../../core/pairing';
import {
  OnboardingError,
  redeemOnboardingSecret,
  redeemPairCode,
  type OnboardingRedemption,
} from '../../db/onboarding-client';
import { extractParam, stripParams, writeCachedIdentity, writeToken } from './identity';

const ONBOARDING_PARAMS: readonly string[] = [ONBOARDING_PARAM];

/** The secret this page load arrived with. Never persisted, never rendered. */
let pending: string | null = null;

/**
 * One in-flight (or settled) redemption per credential. See property 3 above.
 *
 * The key is prefixed with the representation, so a secret and a typed pairing
 * code can never collide on it — they live in different alphabets, but a shared
 * key would still be a silent way for one to answer for the other.
 */
let inFlight: { key: string; promise: Promise<OnboardingRedemption> } | null = null;

/**
 * Store what a redemption returned, before the caller can drop it.
 *
 * A redemption that succeeded server-side and was then lost in a re-render
 * would have burned the surveyor's only code for nothing, so the token and the
 * identity are written the instant they arrive.
 */
function keep(result: OnboardingRedemption): OnboardingRedemption {
  writeToken(result.token);
  writeCachedIdentity(result.surveyor);
  return result;
}

/**
 * Run a redemption at most once per credential — but only while it is in
 * flight or has SUCCEEDED.
 *
 * The memo exists to stop a second request for a code that already worked: a
 * StrictMode double-effect, a camera decoding the same symbol on consecutive
 * frames, a gloved thumb hitting the button twice. All of those would fire a
 * second redemption of a single-use code, and the second is correctly refused
 * as `onboarding_link_used` — a working code reporting itself spent.
 *
 * A FAILED attempt is forgotten, and that is the half that used to be missing.
 * The failure that matters is 'offline': nothing was consumed, the code on the
 * office screen is still good, and the screen therefore offers Try Again — but
 * a memo that held on to the rejected promise would hand the same failure back
 * instantly, forever, and Try Again would be a button that could not work. A
 * refusal is safe to retry too; it is simply refused again, truthfully.
 */
function once(
  key: string,
  run: () => Promise<OnboardingRedemption>,
): Promise<OnboardingRedemption> {
  if (inFlight?.key === key) return inFlight.promise;

  const promise = run()
    .then(keep)
    .catch((cause: unknown) => {
      // Only if nothing newer has taken the slot: a later attempt with a
      // different code must not be cleared by this one's failure arriving late.
      if (inFlight?.promise === promise) inFlight = null;
      throw cause;
    });
  inFlight = { key, promise };
  return promise;
}

/**
 * Take an onboarding secret out of the current URL, if there is one.
 *
 * Returns true when this page load was a scan, so `main.tsx` can send the app
 * to the onboarding screen rather than to a survey list the device cannot fill
 * yet. Called once, before React mounts.
 */
export function claimOnboardingFromLocation(): boolean {
  if (typeof window === 'undefined') return false;

  const found = extractParam(
    window.location.search,
    window.location.hash,
    ONBOARDING_PARAMS,
    looksLikeOnboardingSecret,
  );
  if (!found) return false;

  pending = found;

  const cleanedHash = stripParams(window.location.hash, ONBOARDING_PARAMS);
  const cleaned =
    window.location.pathname +
    stripParams(window.location.search, ONBOARDING_PARAMS) +
    // Whatever the link pointed at, the secret comes off it first. A bare
    // `#o=…` leaves nothing behind, so the onboarding screen is the target.
    (cleanedHash && cleanedHash !== '#' ? cleanedHash : '#/onboard');
  window.history.replaceState(null, '', cleaned || '/#/onboard');
  return true;
}

/** Is there a code waiting to be redeemed on this page load? */
export function hasPendingOnboarding(): boolean {
  return pending !== null;
}

/**
 * Redeem the pending code, at most once per page load.
 *
 * On success the token and the identity are written to the device immediately —
 * before the caller gets a chance to drop the result — because a redemption
 * that succeeded server-side and was then lost in a re-render would have burned
 * the surveyor's only code for nothing.
 *
 * Rejects with `OnboardingError` (`code: 'no_code'`) when there is nothing to
 * redeem, which is what a reload of `#/onboard` looks like.
 */
export async function redeemPendingOnboarding(): Promise<OnboardingRedemption> {
  const secret = pending;
  if (secret === null) {
    throw new OnboardingError('There is no setup code to use on this page.', {
      status: 0,
      code: 'no_code',
    });
  }

  return once(`secret:${secret}`, () => redeemOnboardingSecret(secret));
}

/* ═══════════════════ pairing from INSIDE the installed app ═════════════════ */

/**
 * The onboarding secret inside whatever the camera just decoded (D-019).
 *
 * A QR decodes to a STRING, and the app's own scanner gets that string raw
 * instead of getting a navigation. Three things can be in it:
 *
 *   the setup URL — `https://…/#o=<secret>`, which is what the office prints;
 *   a bare secret — a re-encoded value, accepted because refusing it would be
 *     a puzzle rather than a protection;
 *   anything else — a poster, a parts label, a Wi-Fi code. `null`, so the
 *     scanner says "that is not a setup code" and keeps looking rather than
 *     spending a request on it.
 *
 * The URL is parsed by `extractParam`, the same function that reads a secret
 * out of a real navigation, so a scanned link and an opened link can never
 * disagree about what a setup link means.
 */
export function onboardingSecretFromScan(decoded: string): string | null {
  const text = decoded.trim();
  if (text.length === 0) return null;
  if (looksLikeOnboardingSecret(text)) return text;

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  return extractParam(url.search, url.hash, ONBOARDING_PARAMS, looksLikeOnboardingSecret);
}

/**
 * Redeem a code the surveyor scanned or typed while already in the app (D-019).
 *
 * ── Why this exists at all ──────────────────────────────────────────────────
 * `claimOnboardingFromLocation()` above only works when the app was OPENED by
 * the link — and on iOS a link tapped in Messages or Mail opens in Safari,
 * whose storage container is a different one from the installed Home Screen
 * app's. The token lands in the browser and the installed app, the one the
 * surveyor actually works in, is still unlinked. So the installed app has to be
 * able to take a code in through its own camera or its own keyboard, and this
 * is where both of those arrive.
 *
 * ── Same memoisation, and it matters more here ──────────────────────────────
 * A camera loop can decode the same QR on consecutive frames and a big button
 * can be double-tapped by a gloved hand. Either would fire two redemptions of a
 * single-use code, and the second would be refused as `onboarding_link_used` —
 * a working code reporting itself spent. One promise per credential closes both.
 *
 * The value is never stored. Only the token it is exchanged for is.
 */
export async function redeemScannedSecret(secret: string): Promise<OnboardingRedemption> {
  if (!looksLikeOnboardingSecret(secret)) {
    throw new OnboardingError('That code was not a setup code.', {
      status: 0,
      code: 'not_a_setup_code',
    });
  }
  return once(`secret:${secret}`, () => redeemOnboardingSecret(secret));
}

/** Redeem a typed pairing code. Shape-checked here so junk costs no request. */
export async function redeemTypedPairCode(code: string): Promise<OnboardingRedemption> {
  if (!looksLikePairCode(code)) {
    throw new OnboardingError('That is not a setup code.', {
      status: 0,
      code: 'not_a_setup_code',
    });
  }
  // Keyed on the NORMALISED value, so `bcd fgh jkl` retyped as `BCD-FGH-JKL`
  // is recognised as the same attempt and does not fire a second redemption.
  return once(`code:${normalizePairCode(code)}`, () => redeemPairCode(code));
}

/**
 * Restart the app as a LINKED device.
 *
 * A full reload, and deliberately so. `services.linked` is computed once in
 * src/main.tsx from the token present at boot, and the sync engine's pull half
 * is gated on the same fact. A device that has just paired in-app holds a token
 * that none of them know about yet: the survey list would still show "not
 * linked yet", the sync pill would still read "Not linked", and no assignment
 * would be pulled until something else happened to reboot the app. Rebooting it
 * ourselves is the honest way to make one boolean true everywhere at once.
 *
 * It lands on `#/access`, which is the screen built to say "You're set, Dave"
 * and to pull the work that was waiting — and `replace`, not an assignment, so
 * Back cannot return to a pairing screen for a code that is now spent.
 *
 * Safe offline in principle (the shell is precached), and pairing has just
 * proven there is a connection anyway.
 */
export function restartLinked(): void {
  if (typeof window === 'undefined') return;
  window.location.replace(`${window.location.pathname}${window.location.search}#/access`);
  window.location.reload();
}
